import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu as MenuIcon, X } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { CONTACT } from "@/data/site";

export default function Nav() {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#menu", label: t.nav.menu },
    { href: "#special", label: t.nav.special },
    { href: "#location", label: t.nav.location },
  ];

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 1.4, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-b border-[var(--gold)]/15" : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 md:px-10">
        <a href="#top" className="group flex flex-col leading-none" data-testid="nav-logo">
          <span className="font-serif text-xl tracking-tight text-[var(--text)] md:text-2xl">
            Le Goût <span className="italic text-gold">du Fouta</span>
          </span>
          <span className="font-sans text-[9px] uppercase tracking-[0.34em] text-[var(--text-3)] group-hover:text-[var(--gold)] transition-colors">
            Restaurant Africain · Halal
          </span>
        </a>

        <nav className="hidden items-center gap-9 md:flex">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="relative font-sans text-sm text-[var(--text-2)] transition-colors hover:text-[var(--gold)] after:absolute after:-bottom-1 after:left-0 after:h-px after:w-0 after:bg-[var(--gold)] after:transition-all hover:after:w-full"
              data-testid={`nav-link-${l.href.slice(1)}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            className="flex items-center gap-1 rounded-full border border-[var(--gold)]/40 px-3 py-1.5 font-sans text-xs tracking-wide text-[var(--gold)] transition-colors hover:bg-[var(--gold)]/10"
          >
            <span className={lang === "fr" ? "text-[var(--gold)]" : "text-[var(--text-3)]"}>FR</span>
            <span className="text-[var(--text-3)]">/</span>
            <span className={lang === "en" ? "text-[var(--gold)]" : "text-[var(--text-3)]"}>EN</span>
          </button>

          <a
            href={`tel:${CONTACT.phoneTel}`}
            data-testid="nav-call-btn"
            className="hidden items-center gap-2 bg-[var(--gold)] px-5 py-2.5 font-sans text-sm font-semibold text-[var(--bg)] transition-colors hover:bg-[var(--gold-light)] sm:flex"
          >
            <Phone size={15} /> {CONTACT.phoneDisplay}
          </a>

          <button
            className="md:hidden text-[var(--text)]"
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-mobile-toggle"
            aria-label="menu"
          >
            {open ? <X size={24} /> : <MenuIcon size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="overflow-hidden border-t border-[var(--gold)]/15 bg-black/90 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="border-b border-white/5 py-3 font-serif text-2xl text-[var(--text)]"
                  data-testid={`nav-mobile-link-${l.href.slice(1)}`}
                >
                  {l.label}
                </a>
              ))}
              <a
                href={`tel:${CONTACT.phoneTel}`}
                className="mt-3 flex items-center justify-center gap-2 bg-[var(--gold)] py-3 font-sans font-semibold text-[var(--bg)]"
                data-testid="nav-mobile-call"
              >
                <Phone size={16} /> {CONTACT.phoneDisplay}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
